import { type DefaultNavItem, SideNav } from "@healthos/ui/shell";
import {
  AddressBook,
  ArrowUUpLeft,
  Brain,
  CalendarDots,
  ChatsCircle,
  ChatsTeardrop,
  Clock,
  EnvelopeSimple,
  Flag,
  Footprints,
  Gauge,
  GearSix,
  Handshake,
  Heartbeat,
  MegaphoneSimple,
  PlugsConnected,
  Receipt,
  SlidersHorizontal,
  WaveSine,
} from "@phosphor-icons/react";
import { hasCap, type OperationsMe } from "../../pages/operations/dashboard/_shared";

/** Nav order mirrors the design handoff: work surfaces first, admin last. */
export const OPS_NAV_ITEMS: DefaultNavItem[] = [
  {
    id: "coachees",
    label: "Coachees",
    path: "/operations/coaching/coachees",
    Icon: ChatsTeardrop,
  },
  {
    id: "schedule",
    label: "Schedule",
    path: "/operations/coaching/schedule",
    Icon: CalendarDots,
  },
  {
    id: "availability",
    label: "Availability",
    compactLabel: "Hours",
    path: "/operations/coaching/availability",
    Icon: Clock,
  },
  {
    id: "coaching-ops",
    label: "Coaching",
    path: "/operations/coaching-ops",
    Icon: Handshake,
  },
  {
    id: "dashboard",
    label: "Dashboard",
    compactLabel: "Today",
    path: "/operations/dashboard",
    Icon: Gauge,
  },
  {
    id: "people",
    label: "People",
    path: "/operations/people",
    Icon: AddressBook,
  },
  {
    id: "escalations",
    label: "Escalations",
    path: "/operations/escalations",
    Icon: Flag,
  },
  {
    id: "signals",
    label: "Signals",
    path: "/operations/signals",
    Icon: WaveSine,
  },
  {
    id: "community",
    label: "Community",
    path: "/operations/community",
    Icon: ChatsCircle,
  },
  {
    id: "challenges",
    label: "Challenges",
    path: "/operations/challenges",
    Icon: Footprints,
  },
  {
    id: "broadcasts",
    label: "Broadcasts",
    path: "/operations/broadcasts",
    Icon: MegaphoneSimple,
  },
  {
    id: "billing",
    label: "Billing",
    path: "/operations/billing",
    Icon: Receipt,
  },
  {
    id: "integrations",
    label: "Integrations",
    compactLabel: "Connect",
    path: "/operations/integrations",
    Icon: PlugsConnected,
  },
  {
    id: "integrations-admin",
    label: "Integration admin",
    compactLabel: "Int. admin",
    path: "/operations/admin/integrations",
    Icon: SlidersHorizontal,
  },
  {
    id: "email-templates",
    label: "Email templates",
    compactLabel: "Email",
    path: "/operations/admin/email",
    Icon: EnvelopeSimple,
  },
  {
    id: "cortex",
    label: "Cortex",
    compactLabel: "Cortex",
    path: "/operations/cortex",
    Icon: Brain,
  },
  {
    id: "platform",
    label: "Platform health",
    compactLabel: "Platform",
    path: "/operations/platform",
    Icon: Heartbeat,
  },
  {
    id: "settings",
    label: "Settings",
    path: "/operations/settings",
    Icon: GearSix,
  },
];

/** Exit back to the member surface; side-nav only, pinned under a rule. */
const OPS_EXIT_ITEM: DefaultNavItem = {
  id: "back-to-healthos",
  label: "Back to HealthOS",
  path: "/today",
  Icon: ArrowUUpLeft,
  group: "footer",
};

const NAV_CAPS: Record<string, string> = {
  coachees: "ops:coach",
  schedule: "ops:coach",
  availability: "ops:coach",
  "coaching-ops": "ops:coaching",
  dashboard: "ops:view",
  people: "ops:enrollment",
  escalations: "ops:member_data",
  signals: "ops:member_data",
  community: "ops:view",
  challenges: "ops:view",
  broadcasts: "ops:broadcast",
  billing: "ops:view",
  integrations: "ops:view",
  "integrations-admin": "ops:integrations_admin",
  "email-templates": "ops:integrations_admin",
  cortex: "ops:cortex",
  platform: "ops:admin",
  settings: "ops:admin",
};

export function opsNavItemsFor(me: OperationsMe | null): DefaultNavItem[] {
  return OPS_NAV_ITEMS.filter((item) => hasCap(NAV_CAPS[item.id] ?? "ops:admin", me));
}

/** A coach with no other seat is in their coaching workspace, not Operations. */
export function opsBrandFor(me: OperationsMe | null): string {
  return me?.capabilities.every((c) => c === "ops:coach") ? "Coaching" : "Operations";
}

export function OpsSideNav(props: {
  me: OperationsMe | null;
  items: DefaultNavItem[];
  exit: boolean;
}) {
  const { me } = props;
  const items = [...props.items, ...(props.exit ? [OPS_EXIT_ITEM] : [])];
  return (
    <SideNav
      items={items}
      brand={{ name: opsBrandFor(me), eyebrow: "HealthOS" }}
      navLabel="Operate"
    />
  );
}
